import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Modal from "@mui/material/Modal";
import StepProgress from "../../components/stepper/StepProgress";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import LoadingOverlay from "../../components/overlays/LoadingOverlay";
import AppNavigation from "../../components/navigation/appNavigation/AppNavigation";
import { handleFileChange } from "../../utils/SubmitContent/fileHandling";
import {
  handleSubmit,
  handleSubmitContent,
} from "../../utils/SubmitContent/handleSubmit";
import { useWordCount } from "../../utils/SubmitContent/wordCount";
import { navigateToQuestionSelection } from "../../utils/SubmitContent/handleQuestionSelectionAPICall.js";
import TitleAndDomainInput from "../../components/submitContentComponents/TitleAndDomainInput";
import HelpingPointer from "../../components/submitContentComponents/HelpingPointers";
import useCanvasDetection from "../../hooks/useCanvasDetection";
import { commonErrorToastMessage } from "../../components/toastify/AllToastifyErrors";
import QuillionzProUpgradeModal from "../../components/modals/QuillionzProUpgradeModal";
import TrySampleText from "../../components/submitContentComponents/TrySampleText.jsx";

import { franc } from "franc";
import { canvasUserLogin } from "../../api/userLogin";
import { youtubeVideo } from "../../api/youtubeVideo";
import SubmitContentUnlimitedModal from "../../components/modals/SubmitContentUnlimitedModal";
import ConfirmFileUploadModal from "../../components/modals/ConfirmFileUploadModal";
import ContentInfoModal from "../SubmitContent/ContentInfoModal.jsx";
import ContentErrorModal from "../SubmitContent/ContentErrorModal.jsx";
import { fetchSampleText } from "../../api/fetchSampleText.js";
import { getQuestionLimit, getRequiredSets } from "../../utils/QuestionLimit.js";
import { getDecryptedBatch,setEncrypted,setEncryptedBatch } from "../../utils/cryptoUtils.js";

const SubmitContent = ({ hasCanvas }) => {
  const wordsMinLimit = 300;
  const wordsMaxLimit = 15000;
  const MAX_QUESTIONS_PER_CYCLE = 30;

  const location = useLocation();
  const navigate = useNavigate();
  const isCanvas = useCanvasDetection();

  // ── Non-sensitive UI state ────────────────────────────────────────────────
  const [selectedFile, setSelectedFile] = useState(null);
  const [content, setContent] = useState("");
  const [uploadedContent, setUploadedContent] = useState("");
  const [domain, setDomain] = useState("Others");
  const [isNetworkError, setIsNetworkError] = useState(false);
  const [title, setTitle] = useState("");
  const [youtubeModalResponseData, setYoutubeModalResponseData] = useState("");
  const [isYoutubeModalOpen, setIsYoutubeModalOpen] = useState(false);
  const [showManageSubscription, setShowManageSubscription] = useState(false);
  const [showGoForUnlimitedModal, setShowGoForUnlimitedModal] = useState(false);
  const [youtubeLink, setYoutubeLink] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [isSampleButtonClicked, setIsSampleButtonClicked] = useState(false);
  const [sampleButtonText, setSampleButtonText] = useState("Try a Sample Text");
  const [doc_id, setDoc_id] = useState("");
  const [responseData, setResponseData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isTextLoading, setIsTextLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [loadingContent, setLoadingContent] = useState(false);
  const [isDropupOpen, setIsDropupOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(true);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isContentInfoModalOpen, setIsContentInfoModalOpen] = useState(false);
  const [isContentErrorModalOpen, setIsContentErrorModalOpen] = useState(false);
  const [subscriptionPlan, setSubscriptionPlan] = useState("");
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const { wordCount, updateWordCount } = useWordCount(content);

  const titleInputRef = useRef(null);
  const domainSelectRef = useRef(null);
  const textareaRef = useRef();
  const fileInputRef = useRef(null);

  // ── Encrypted localStorage values — all live in state ────────────────────
  const [userEmail, setUserEmail] = useState("");
  const [isGptActive, setIsGptActive] = useState("");
  const [userGPTKey, setUserGPTKey] = useState("");
  const [userAttempts, setUserAttempts] = useState(0);
  const [Version, setVersion] = useState("");
  const [isPlanExpired, setIsPlanExpired] = useState("false");
  const [TIERTYPE, setTIERTYPE] = useState("");
  const [remainingQuestionSets, setRemainingQuestionSets] = useState(0);
  const [remainingDaysInMonth, setRemainingDaysInMonth] = useState("");
  const [planExpiryDate, setPlanExpiryDate] = useState("");

  // ── Step 1: Decrypt all needed values once on mount ──────────────────────
  useEffect(() => {
    const loadSecureData = async () => {
      const {
        email: decryptedEmail,
        isGptActive: decryptedGptActive,
        UserAttempts: decryptedAttempts,
        Version: decryptedVersion,
        isPlanExpired: decryptedExpired,
        TIERTYPE: decryptedTierType,
        monthEndDate: decryptedMonthEnd,
        planExpiryDate: decryptedExpiry,
        subscriptionPlan: decryptedSubPlan,
        userGPTKey:decryptedUserGPTKey,
      } = await getDecryptedBatch([
        "email",
        "isGptActive",
        "UserAttempts",
        "Version",
        "isPlanExpired",
        "TIERTYPE",
        "monthEndDate",
        "planExpiryDate",
        "subscriptionPlan",
        "userGPTKey"
      ]);

      setUserEmail(decryptedEmail ?? "");
      setUserGPTKey(decryptedUserGPTKey || "LIMITED");
      setIsGptActive(Number(decryptedGptActive) || 0);
      setUserAttempts(Number(decryptedAttempts) || 0);
      setVersion(decryptedVersion ?? "");
      // Normalise to string so existing  === "true" / === "false"  checks keep working
      setIsPlanExpired(String(decryptedExpired ?? "false"));
      setTIERTYPE(decryptedTierType ?? "");
      setRemainingQuestionSets(Number(decryptedAttempts) || 0);
      setRemainingDaysInMonth(decryptedMonthEnd ?? "");
      setPlanExpiryDate(decryptedExpiry ?? "");

      // Subscription plan label
      const planNumber = parseInt(decryptedSubPlan);
      const planMap = { 1: "Monthly", 3: "Quarterly", 12: "Annual" };
      setSubscriptionPlan(planMap[planNumber] || "Free");

      setIsDataLoaded(true);
    };

    loadSecureData();
  }, []);

  // ── Listen for UserAttempts changes from other tabs ───────────────────────
  useEffect(() => {
    const onStorage = async (e) => {
      if (e.key === "UserAttempts") {
        // Re-decrypt on change (the new value in localStorage is still encrypted)
        const fresh = await getDecryptedBatch(["UserAttempts"]);
        setUserAttempts(Number(fresh.UserAttempts) || 0);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // ── Canvas login flow — writes to encrypted storage after login ───────────
  useEffect(() => {
    (async () => {
      if (hasCanvas) {
        const { data, status } = await canvasUserLogin();
        if ([200, 201].includes(status)) {
          const {
            token,
            username,
            email,
            remainingQuestionSets: rqs,
            currentDate,
            Usergpt_key,
            userId,
            isGptActive: iga,
            Plan_Validity_Subscription,
            ExpiryDate,
            remainingDaysInMonth: rdim,
            calculatedDate,
            isPlanExpired: ipe,
          } = data;

          const numericRemaining = Number(rqs) || 0;

          // ✅ Write all canvas login data to encrypted localStorage
          await setEncryptedBatch({
            token,
            email,
            username,
            Id: userId,
            UserLoggedIn: currentDate,
            userGPTKey: Usergpt_key,
            isGptActive: iga,
            remainingDaysInMonth: rdim,
            calculatedDate,
            ExpiryDate,
            Plan_Validity_Subscription,
            isPlanExpired: ipe,
            UserAttempts: numericRemaining,
          });

          // Sync state
          setUserGPTKey(Usergpt_key);
          setIsGptActive(iga);
          setUserAttempts(numericRemaining);
          setIsPlanExpired(String(ipe ?? "false"));
        }
      }
    })();
    textareaRef?.current?.focus();
  }, []);

  // ── Helpers (now use state instead of localStorage.getItem) ──────────────
  const getNumericUserAttempts = () => userAttempts;
  const getVersionNormalized = () => (Version || "").toLowerCase();

  const user_tier =
    TIERTYPE === "tier2_monthly" || TIERTYPE === "tier2_annually" ? 2 : 1;

  const getFormattedMonth = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
      day: "numeric",
    });
  };
  const formattedMonthEndDate = getFormattedMonth(remainingDaysInMonth);

  const toggleDropup = () => setIsDropupOpen((prev) => !prev);

  const handleChange = (e) => setChecked(e.target.checked);
  const handleDropdownChange = (e) => setDomain(e.target.value);
  const handleYoutubeModalOpen = () => {
    setIsYoutubeModalOpen(true);
    setShowTooltip(false);
  };
  const activeStep = 1;

  const isWordCountLessThan300 =
    (wordCount < wordsMinLimit || wordCount > wordsMaxLimit) && wordCount >= 1;
  const wordCountClass = isWordCountLessThan300 ? "text-red-500" : "";

  // ── getSampleText uses userEmail from state ───────────────────────────────
  async function getSampleText(category, subCategory) {
    const { data, status } = await fetchSampleText(
      userEmail,
      category,
      subCategory,
    );
    if ([200, 201].includes(status)) return data;
  }

  const handleSampleButtonClick = async (selectedOption) => {
    setIsSampleButtonClicked(true);
    let titleVal = "",
     category = "",
     subCategory = "",
    buttonText = selectedOption;

    switch (selectedOption) {
      case "Private Pension":
        titleVal = "Private Pension Policy";
        category = "Retirement Planning & Pensions";
        subCategory = "Pension Plans";
        break;
      case "Tracker Rate Mortgage":
        titleVal = "Tracker Rate Mortgage Agreement";
        category = "Small Business Financial Management";
        subCategory = "Business Funding";
        break;
      case "Fixed Rate Mortgage":
        titleVal = "Fixed Rate Mortgage Agreement";
        category = "Personal Budgeting & Debt Management";
        subCategory = "Secured Lending";
        break;
      case "Home Insurance":
        titleVal = "Home Contents Insurance Policy";
        category = "Insurance & Risk Management";
        subCategory = "Property Insurance";
        break;
      case "Car Insurance":
        titleVal = "Comprehensive Car Insurance Policy";
        category = "Insurance & Risk Management";
        subCategory = "Liability Insurance";
        break;
      default:
        titleVal = "Exploring Scientific Frontiers";
        category = "Others";
        subCategory = "Others";
        buttonText = "Try a Sample Text";
    }

    const sample_data = await getSampleText(category, subCategory);
    setTitle(titleVal);
    setContent(cleanApiText(sample_data?.context));
    setSampleButtonText(buttonText);
    // These are non-sensitive session keys — plain setItem is fine
    localStorage.setItem("isSampleButtonClicked", true);
    localStorage.setItem("title", titleVal);
    localStorage.setItem("category", category);
    localStorage.setItem("subCategory", subCategory);
    localStorage.setItem("content", sample_data?.context);
    localStorage.setItem("doc_id", sample_data?.doc_id);
  };

  const handleTextAreaChange = (event) => {
    if (isSampleButtonClicked) return;
    const newContent = event.target.value.replace(/\s+/g, " ");
    const words = newContent.trim().split(/\s+/);
    const trimmed = words.slice(0, wordsMaxLimit).join(" ");
    const wc = Math.min(words.length, wordsMaxLimit);

    if (words.length > wordsMaxLimit) {
      setContent(trimmed);
      updateWordCount(trimmed);
      const QAreqbody = JSON.parse(localStorage.getItem("QAreqbody") || "{}");
      QAreqbody.text = trimmed;
      QAreqbody.total_words = wc;
      localStorage.setItem("QAreqbody", JSON.stringify(QAreqbody));
    } else {
      setContent(newContent);
      updateWordCount(newContent);
      const QAreqbody = JSON.parse(localStorage.getItem("QAreqbody") || "{}");
      QAreqbody.text = newContent;
      QAreqbody.total_words = wc;
      localStorage.setItem("QAreqbody", JSON.stringify(QAreqbody));
    }
  };

  const handleTextAreaChangeForFileUpload = () => updateWordCount("");

  const handleManageSubscriptionModal = () => navigate("/userinfo");
  const handleGoForUnlimitedModal = () => setShowGoForUnlimitedModal(true);

  const handleProceedClick = () => {
    if (Version && Version.toLowerCase() === "free" && !isSampleButtonClicked) {
      setShowManageSubscription(true);
      return;
    }
    if (!title.trim() && !domain.trim() && !content.trim()) {
      commonErrorToastMessage(
        "Please add title, domain and content to proceed.",
      );
      return;
    }
    if (!title.trim()) {
      commonErrorToastMessage("Please add title for your content.");
      return;
    }
    if (!domain.trim()) setDomain("Other");
    if (!content.trim()) {
      commonErrorToastMessage("Please provide the content to proceed.");
      return;
    }
  };

  const handleContentInfoModalCancel = () => setIsContentInfoModalOpen(false);
  const handleContentErrorModalCancel = () => setIsContentErrorModalOpen(false);

  const handleContentInfoModalOk = async () => {
    try {
      setIsContentInfoModalOpen(false);
      const attempts = getNumericUserAttempts();
      const isSample =
        isSampleButtonClicked ||
        localStorage.getItem("isSampleButtonClicked") === "true";

      if (!isSample && Version !== "Free" && attempts <= 0) {
        commonErrorToastMessage("Your Count have been expired.");
        return;
      }
      if (!title.trim()) {
        commonErrorToastMessage("Please add title for your content.");
        return;
      }
      setIsLoading(true);

      const QAreqbodyRaw = localStorage.getItem("QAreqbody");
      let hasDocId = false;
      if (QAreqbodyRaw) {
        try {
          const QAobj =
            typeof QAreqbodyRaw === "string"
              ? JSON.parse(QAreqbodyRaw)
              : QAreqbodyRaw;
          hasDocId = !!(
            (QAobj && QAobj.doc_id) ||
            QAobj?.fullResponse?.data?.doc_id
          );
        } catch {
          hasDocId = false;
        }
      }

      if (!hasDocId) {
        await handleSubmitContentButton();
      } else {
        await new Promise((resolve) => setTimeout(resolve, 5000));
        navigate("/QuestionsSelection");
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error("Error uploading text:", error);
    }
  };

  const getRequiredSetsForWords = (wc) => getRequiredSets(Number(wc) || 0);

  const openContentModalForWordCount = (wc) => {
    const requiredSets = getRequiredSetsForWords(wc);
    if (isSampleButtonClicked || getVersionNormalized() === "free") {
      setIsContentInfoModalOpen(true);
      return;
    }
    const attempts = getNumericUserAttempts();
    if (requiredSets > attempts) {
      setIsContentErrorModalOpen(true);
    } else {
      setIsContentInfoModalOpen(true);
    }
  };

  const handleFileInputChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        commonErrorToastMessage("File size must be less than 10MB.");
        fileInputRef.current.value = "";
        setShowTooltip(false);
        fileInputRef.current.click();
        return;
      }
      setIsConfirmModalOpen(true);
      setSelectedFile(file);
    }
    if (isConfirmModalOpen) {
      setIsConfirmModalOpen(false);
      setContent("");
      setLoadingContent(true);
      setContent((prevContent) => {
        const words = event.target.value.trim().split(/\s+/);
        const newContent = event.target.value;
        return words.length <= wordsMaxLimit ? newContent : prevContent;
      });
    }
  };

  const handleConfirmUpload = async () => {
    setIsConfirmModalOpen(false);
    if (selectedFile) {
      try {
        const newContent = await handleFileChange(
          selectedFile,
          title,
          content,
          wordCount,
          updateWordCount,
          setResponseData,
          setSelectedFile,
          setIsFileUploaded,
          setLoadingContent,
          setIsLoading,
          setTitle,
          setContent,
          setDomain,
          setDoc_id,
          domain,
        );
        if (!newContent) {
          setContent("");
          setSelectedFile(null);
        }
        if (newContent !== null) {
          localStorage.setItem(
            "QAreqbody",
            JSON.stringify(newContent.fullResponse.data),
          );
          setContent(cleanApiText(newContent?.text));
        }
      } catch (error) {
        setContent("");
        console.error("Error while uploading PDF:", error);
      }
    }
  };

  const handleCancelUpload = () => {
    setIsConfirmModalOpen(false);
    setSelectedFile(null);
    setTitle("");
    setContent("");
    setLoadingContent(false);
  };

  const handleUploadButtonClick = () => {
    if (!title) {
      commonErrorToastMessage(
        "Please provide the title before you upload the file.",
      );
      return;
    }
    const fileInput = fileInputRef.current;
    if (!fileInput) {
      commonErrorToastMessage("File input is not available.");
      return;
    }
    if (fileInput.files.length === 0) {
      fileInput.click();
    } else {
      const maxSize = 10 * 1024 * 1024;
      if (fileInput.files[0].size > maxSize) {
        commonErrorToastMessage("File size must be less than 10MB.");
      } else {
        fileInput.click();
      }
    }
  };

  const handleSubmitContentButton = async () => {
    await handleSubmitContent({
      title,
      domain,
      wordCount,
      selectedFile,
      uploadedContent,
      content,
      setContent,
      setIsLoading,
      setIsTextLoading,
      setTitle,
      setDomain,
      setIsNetworkError,
      setResponseData,
      titleInputRef,
      domainSelectRef,
      navigate,
    });
  };

  const handleQSSNavigation = async () => {
    await navigateToQuestionSelection(
      title,
      domain,
      wordCount,
      doc_id,
      content,
      responseData,
      titleInputRef,
      domainSelectRef,
      setIsLoading,
      setIsTextLoading,
      navigate,
      selectedFile,
    );
  };

  function cleanApiText(str = "") {
    return str
      .replace(/\\/g, "")
      .replace(/\*\*(.*?)\*\*/g, "$1")
      .replace(/\*(.*?)\*/g, "$1");
  }

  return (
    <div className=" h-[100vh] overflow-hidden">
      {/* {isCanvas ? null : <AppNavigation />} */}
      {/* App Navigation */}
      <AppNavigation
        setTitle={setTitle}
        setContent={setContent}
        setDomain={setDomain}
        setSelectedFile={setSelectedFile}
      />
      {/* Stepper component (present of rightmost side) */}
      <div className="w-auto h-auto step-progress">
        {/* Pass the activeStep value to the StepProgress component */}
        <StepProgress activeStep={activeStep} />
      </div>
      <div className={`bg-white h-[81vh] select-none `}>
        <div className=" flex ">
          {/* Conditionally displaying the "TitleAndDomainInput" and "HelperPointer" components */}
          {!isDataLoaded ? (
            // Show a neutral loading state — no flash of wrong UI
            <div className="w-full h-[81vh] flex items-center justify-center">
              <span className="loader"></span>{" "}
              {/* or any spinner you already use */}
            </div>
          ) : !isPlanExpired || isPlanExpired === "false" ? (
            remainingQuestionSets !== 0 || Number(isGptActive) === 1 || userGPTKey === "LIMITED"? (
              <div className=" w-full  h-auto px-6 pt-0 rounded-2xl mx-5">
                <TitleAndDomainInput
                  title={title}
                  setTitle={setTitle}
                  titleInputRef={titleInputRef}
                  checked={checked}
                  handleChange={handleChange}
                  domain={domain}
                  setDomain={setDomain}
                  handleDropdownChange={handleDropdownChange}
                  domainSelectRef={domainSelectRef}
                />

                <div className="mt-[10px]">
                  {/* Text area for content input starts here */}
                  <div
                    className={`p-5 mx-auto custom-scrollbar my-area opacity-80 ${
                      isCanvas ? "h-[63vh]" : "h-[56vh] "
                    }`}
                    style={{
                      minHeight: "33vh",
                      maxHeight: "81vh",
                      width: "90%",
                      marginTop: "3px",
                      marginBottom: "3px",
                      background: "#f9fbff",
                      // If the valid file is uploaded or any text is entered then size of text area changes accordingly (myAreaHeight function)
                      // height: "70vh",
                      overflowY: content ? "hidden" : "hidden", // Add this line to enable vertical scrolling
                      border: "2px solid #ccbfff", // 👈 add this line
                      borderRadius: "8px", // optional: rounded corners
                    }}
                  >
                    {loadingContent ? (
                      <div className="mt-[30%]">
                        <HelpingPointer Version={Version} UserAttempts={userAttempts}  />
                      </div>
                    ) : (
                      <textarea
                        className={`w-full h-full resize-none bg-[#f9fbff]  p-2 text-[#212529] font-normal text-lg leading-6 outline-none custom-scrollbar  ${
                          isSampleButtonClicked ? "" : ""
                        }`}
                        value={content}
                        ref={textareaRef}
                        onChange={handleTextAreaChange}
                      ></textarea>
                    )}

                    {/* Pointers */}
                    {!selectedFile && !content && (
                      <div className="">
                        <HelpingPointer
                          isSampleButtonClicked={isSampleButtonClicked}
                          content={content}
                          handleTextAreaChange={
                            handleTextAreaChangeForFileUpload
                          }
                          handleUploadButtonClick={handleUploadButtonClick}
                          handleFileInputChange={handleFileInputChange}
                          handleYoutubeModalOpen={handleYoutubeModalOpen}
                          isFileUploaded={isFileUploaded}
                          textareaRef={textareaRef}
                          fileInputRef={fileInputRef}
                          Version={Version}           
                          UserAttempts={userAttempts}
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="mt-[13px]">
                      <div className="flex  h-[13vh] pr-[35px] align-middle justify-center place-content-center border-t-[1px] ">
                        <div className="w-[45%] ">
                          <p
                            className={`font-semibold mt-2 text-xs ${wordCountClass} text-[#777e8d] `}
                          >
                            *Total words considered : {wordCount} (Word count
                            must be between {wordsMinLimit}-{wordsMaxLimit} for
                            quiz generation.)
                          </p>

                          <p className="text-[11px] text-[#777e8d] pl-[5px]">
                            English Content only.
                          </p>
                        </div>

                        <div className="mt-3  w-[50%] flex justify-end align-middle gap-10">
                          {Version === "Free" ? (
                            <div className="pt-2.5">
                              {" "}
                              <TrySampleText
                                handleSampleButtonClick={
                                  handleSampleButtonClick
                                }
                                isDropupOpen={isDropupOpen}
                                toggleDropup={toggleDropup}
                                sampleButtonText={sampleButtonText}
                              />
                            </div>
                          ) : null}

                          <div className="">
                            {selectedFile || content ? (
                              ""
                            ) : (
                              <div className="flex gap-11">
                                <Tooltip
                                  title="Submit"
                                  arrow
                                  placement="top"
                                  TransitionComponent={Zoom}
                                  TransitionProps={{ timeout: 300 }}
                                >
                                  <button
                                    className="rounded py-2 mt-2 h-[35px] hover:opacity-90 bg-[#c6530c] text-white text-sm  px-4  "
                                    onClick={handleProceedClick}
                                  >
                                    Submit
                                  </button>
                                </Tooltip>
                              </div>
                            )}

                            {selectedFile ? (
                              <Tooltip
                                title="Submit your PDF content"
                                arrow
                                placement="top"
                                TransitionComponent={Zoom}
                                TransitionProps={{ timeout: 300 }}
                              >
                                <button
                                  className={`rounded mt-2 h-[35px] hover:opacity-90 ${
                                    isTextLoading
                                      ? "bg-[#0c3f5a] "
                                      : "bg-[#c6530c]"
                                  } text-white px-4 flex place-content-center align-middle justify-center flex-col`}
                                  onClick={() => {
                                    if (
                                      userAttempts <= 0 &&
                                      isSampleButtonClicked &&
                                      Version.toLowerCase() !== "free"
                                    ) {
                                      if (userAttempts <= 0) {
                                        commonErrorToastMessage(
                                          "Want to create more? Upgrade your plan!",
                                        );
                                        return;
                                      }
                                      setShowManageSubscription(true);
                                      return;
                                    }
                                    if (
                                      Version === "Free" ||
                                      Version === "free"
                                    ) {
                                      if (
                                        Version &&
                                        Version.toLowerCase() === "free" &&
                                        !isSampleButtonClicked
                                      ) {
                                        setShowManageSubscription(true);
                                        return;
                                      }
                                    }
                                    if (wordCount < wordsMinLimit) {
                                      commonErrorToastMessage(
                                        "Word count must be at least 300 for quiz generation.",
                                      );
                                      return;
                                    }
                                    openContentModalForWordCount(wordCount);
                                  }}
                                >
                                  {isTextLoading ? (
                                    <>
                                      <span className="loader"></span>
                                      <span className=" ms-4">
                                        Processing Content
                                      </span>
                                    </>
                                  ) : (
                                    "Submit"
                                  )}
                                </button>
                              </Tooltip>
                            ) : content ? (
                              <Tooltip
                                title="Submit your content"
                                arrow
                                placement="top"
                                TransitionComponent={Zoom}
                                TransitionProps={{ timeout: 300 }}
                              >
                                <button
                                  className={`rounded mt-2 h-[7vh] hover:opacity-90 ${
                                    isTextLoading
                                      ? "bg-[#0c3f5a] "
                                      : "bg-[#c6530c]"
                                  } text-white px-4 flex place-content-center align-middle justify-center flex-col`}
                                  onClick={() => {
                                    if (
                                      getNumericUserAttempts() <= 0 &&
                                      !isSampleButtonClicked &&
                                      getVersionNormalized() !== "free"
                                    ) {
                                      commonErrorToastMessage(
                                        "Want to create more? Upgrade your plan!",
                                      );
                                      setShowManageSubscription(true);
                                      return;
                                    }
                                    if (
                                      Version === "Free" ||
                                      Version === "free"
                                    ) {
                                      if (
                                        Version &&
                                        Version.toLowerCase() === "free" &&
                                        !isSampleButtonClicked
                                      ) {
                                        setShowManageSubscription(true);
                                        return;
                                      }
                                    }
                                    if (wordCount < wordsMinLimit) {
                                      commonErrorToastMessage(
                                        "Word count must be at least 300 for quiz generation.",
                                      );
                                      return;
                                    }
                                    openContentModalForWordCount(wordCount);
                                  }}
                                >
                                  {isTextLoading ? (
                                    <>
                                      <span className="loader "></span>
                                      <span className="ms-5 ">
                                        Processing Content
                                      </span>
                                    </>
                                  ) : (
                                    "Submit"
                                  )}
                                </button>
                              </Tooltip>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Text area for content input ends here */}
              </div>
            ) : (
              // This screen will appear when a user reaches their monthly limit of 25 question sets.
              <div className=" w-full h-[81vh] m-auto flex flex-col justify-center align-middle items-center place-content-center  px-6 pt-4 rounded-2xl mx-5">
                <div className="border rounded-xl p-5 shadow-xl hover:shadow-myShadow2 flex flex-col justify-center align-middle items-center place-content-center">
                  <i className="fa-solid fa-triangle-exclamation text-red-400 fa-5x mb-4"></i>

                  <p>
                    You have{" "}
                    <span className="font-semibold text-red-500">
                      0 out of 5
                    </span>{" "}
                    Quiz sets available for this month till{" "}
                    <span className="font-semibold">{planExpiryDate}.</span>
                  </p>

                  <p className="mt-5">
                    For more Quiz sets, upgrade to the{" "}
                    <span className="font-semibold"> plan</span>.
                  </p>

                  <p className="mt-5">
                    To change subscription duration{" "}
                    <Tooltip
                      title="Manage Subscription"
                      arrow
                      placement="top"
                      TransitionComponent={Zoom}
                      TransitionProps={{ timeout: 300 }}
                    >
                      <button
                        className=" text-[#f4833d]  underline hover:no-underline"
                        onClick={handleManageSubscriptionModal}
                      >
                        Click here
                      </button>
                    </Tooltip>
                    .
                  </p>

                  <div className="flex">
                    {Version === "Free" ? (
                      <>
                        <p>
                          <Tooltip
                            title="Purchase plan"
                            arrow
                            placement="top"
                            TransitionComponent={Zoom}
                            TransitionProps={{ timeout: 300 }}
                          >
                            <button
                              className="mt-4 px-6 py-2 bg-[#f4833d] text-white rounded hover:bg-[#e06a21] "
                              onClick={handleGoForUnlimitedModal}
                            >
                              Purchase plan
                            </button>
                          </Tooltip>
                        </p>
                      </>
                    ) : (
                      <>
                        <p>
                          <Tooltip
                            title="Upgrade to plan"
                            arrow
                            placement="top"
                            TransitionComponent={Zoom}
                            TransitionProps={{ timeout: 300 }}
                          >
                            <button
                              className="mt-4 px-6 py-2 bg-[#f4833d] text-white rounded hover:bg-[#e06a21] "
                              onClick={handleGoForUnlimitedModal}
                            >
                              Upgrade to plan
                            </button>
                          </Tooltip>
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )
          ) : (
            // This screen will appear when a user reaches their monthly limit of 25 question sets.
            <div className=" w-full h-[81vh] m-auto flex flex-col justify-center align-middle items-center place-content-center  px-6 pt-4 rounded-2xl mx-5">
              <div className="border rounded-xl p-5 shadow-xl hover:shadow-myShadow2 flex flex-col justify-center align-middle items-center place-content-center">
                <i className="fa-solid fa-triangle-exclamation text-red-400 fa-5x mb-4"></i>
                <p className="mt-5">Want to create more? Upgrade your plan!</p>
                {isPlanExpired ? (
                  ""
                ) : (
                  <p className="mt-5">
                    For more quiz sets, upgrade to the{" "}
                    <span className="font-semibold">plan</span>.
                  </p>
                )}

                {isPlanExpired ? (
                  <p className="mt-5">
                    To change your subscription duration, please click the
                    button below.
                  </p>
                ) : (
                  <p className="mt-5">
                    To change your subscription duration, please click the
                    button below
                    <Tooltip
                      title="Manage Subscription"
                      arrow
                      placement="top"
                      TransitionComponent={Zoom}
                      TransitionProps={{ timeout: 300 }}
                    >
                      <button
                        className=" text-[#0d3e58]  underline hover:no-underline"
                        onClick={handleManageSubscriptionModal}
                      >
                        Click here
                      </button>
                    </Tooltip>
                    .
                  </p>
                )}

                <div className="flex    ">
                  {isPlanExpired || isPlanExpired === "undefined" ? (
                    <p className="">
                      {" "}
                      <Tooltip
                        title="Manage Subscription"
                        arrow
                        placement="top"
                        TransitionComponent={Zoom}
                        TransitionProps={{ timeout: 300 }}
                      >
                        <button
                          className="rounded mt-4 px-6 py-2 bg-[#0d3e58] text-white  hover:bg-[#0a6697] "
                          onClick={handleManageSubscriptionModal}
                        >
                          Subscribe Now
                        </button>
                      </Tooltip>
                    </p>
                  ) : (
                    <>
                      <p>
                        <Tooltip
                          title="Upgrade to plan"
                          arrow
                          placement="top"
                          TransitionComponent={Zoom}
                          TransitionProps={{ timeout: 300 }}
                        >
                          <button
                            className="mt-4 px-6 py-2 bg-[#f4833d] text-white rounded hover:bg-[#e06a21] "
                            onClick={handleGoForUnlimitedModal}
                          >
                            Upgrade to plan
                          </button>
                        </Tooltip>
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Loading overlay */}
        {isLoading && <LoadingOverlay />}

        {/* Render the Go Premium Modal component */}
        <QuillionzProUpgradeModal
          isOpen={showManageSubscription}
          onClose={() => setShowManageSubscription(false)}
        />

        {/* If a user reaches their monthly limit of 25 question sets, they can open the modal for "Unlimited Question Sets"." */}
        <SubmitContentUnlimitedModal
          open={showGoForUnlimitedModal}
          onClose={() => setShowGoForUnlimitedModal(false)}
          hideFirstPartForSubmitPage={showGoForUnlimitedModal}
          setShowGoForUnlimitedModal={setShowGoForUnlimitedModal}
        />

        <ConfirmFileUploadModal
          isOpen={isConfirmModalOpen}
          onConfirm={handleConfirmUpload}
          onCancel={handleCancelUpload}
          fileName={selectedFile ? selectedFile.name : ""}
        />

        <ContentInfoModal
          open={isContentInfoModalOpen}
          onClose={handleContentInfoModalCancel}
          handleContentInfoModalOk={handleContentInfoModalOk}
          wordCount={wordCount}
        />
        <ContentErrorModal
          open={isContentErrorModalOpen}
          onClose={handleContentErrorModalCancel}
        />
      </div>
    </div>
  );
};

export default SubmitContent;
